/**
 * SheetPickerDropdown - Module-grouped sheet picker for cross-sheet references
 *
 * Story 6.6: Implement Cross-Sheet References
 * - AC-6.6.1: Sheet Reference Picker UI with module grouping
 * - AC-6.6.6: Integration with cross-sheet autocomplete
 *
 * Displays available sheets grouped by module, with current sheet marked.
 */

import React from 'react';
import '../styles/formulaBar.css';

/**
 * Sheet metadata for picker display
 * AC-6.6.1: Lightweight list (id, name, module) of all accessible sheets
 */
export interface SheetMetadata {
  id: string;
  name: string;
  moduleId?: string;
  moduleName?: string;
}

/**
 * Grouped sheets by module for display
 */
interface SheetGroup {
  moduleName: string;
  moduleId: string;
  sheets: SheetMetadata[];
}

interface SheetPickerDropdownProps {
  /** All available sheets */
  sheets: SheetMetadata[];
  /** Name of the currently active sheet */
  currentSheetName?: string;
  /** Callback when a sheet is selected */
  onSelect: (sheetName: string) => void;
  /** Callback to close the dropdown */
  onClose: () => void;
}

/**
 * Group sheets by module name
 * Sheets without a module are grouped under "Other"
 */
function groupSheetsByModule(sheets: SheetMetadata[]): SheetGroup[] {
  const groupMap = new Map<string, SheetGroup>();

  sheets.forEach((sheet) => {
    const moduleKey = sheet.moduleId || '__other__';
    const moduleName = sheet.moduleName || 'Other';

    if (!groupMap.has(moduleKey)) {
      groupMap.set(moduleKey, {
        moduleName,
        moduleId: moduleKey,
        sheets: [],
      });
    }

    groupMap.get(moduleKey)!.sheets.push(sheet);
  });

  // Sort sheets within each group alphabetically
  groupMap.forEach((group) => {
    group.sheets.sort((a, b) => a.name.localeCompare(b.name));
  });

  // Return groups sorted by module name, with "Other" at the end
  return Array.from(groupMap.values()).sort((a, b) => {
    if (a.moduleId === '__other__') return 1;
    if (b.moduleId === '__other__') return -1;
    return a.moduleName.localeCompare(b.moduleName);
  });
}

/**
 * SheetPickerDropdown Component
 *
 * AC-6.6.1: Sheet picker dropdown grouped by module
 * - Current sheet marked with "(current)" badge
 * - Sheets sorted alphabetically within each module group
 */
export const SheetPickerDropdown: React.FC<SheetPickerDropdownProps> = ({
  sheets,
  currentSheetName,
  onSelect,
  onClose,
}) => {
  const groups = groupSheetsByModule(sheets);

  const handleSelect = (sheetName: string) => {
    onSelect(sheetName);
    onClose();
  };

  // Close on click outside (keyboard already handled via Escape in parent)
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.formula-sheet-dropdown')) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="formula-sheet-dropdown" role="listbox" aria-label="Select sheet">
      <div className="formula-sheet-dropdown-header">Insert sheet reference:</div>

      {groups.map((group) => (
        <div key={group.moduleId} className="formula-sheet-group">
          {/* Module header - only show if more than one group */}
          {groups.length > 1 && (
            <div className="formula-sheet-group-header">{group.moduleName}</div>
          )}

          {/* Sheets in this module */}
          {group.sheets.map((sheet) => {
            const isCurrent = sheet.name === currentSheetName;
            return (
              <div
                key={sheet.id}
                role="option"
                aria-selected={isCurrent}
                className={`formula-sheet-option ${isCurrent ? 'current' : ''}`}
                onClick={() => handleSelect(sheet.name)}
              >
                <span className="formula-sheet-option-name">{sheet.name}</span>
                {isCurrent && (
                  <span className="formula-sheet-current-badge">current</span>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {sheets.length === 0 && (
        <div className="formula-sheet-option text-gray-400 italic cursor-default">
          No sheets available
        </div>
      )}
    </div>
  );
};

export default SheetPickerDropdown;
